// src/components/Header.js
import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { siteData } from "../data/siteData";

const Header = ({ startTransition }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Handle navigation with transition for All pages
  const handleNavigation = (e, item) => {
    if (startTransition && item.path) {
      e.preventDefault();
      startTransition(item.path);
    }
    // For other links, use default Link behavior
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Split main nav around the centered logo
  const mainItems = siteData.navigation.main || [];

  return (
    <header
      ref={headerRef}
      className={`fixed top-3 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
        scrolled ? "scrolled" : ""
      } max-w-6xl w-[95vw] px-4`}
    >
      <div className="w-full px-2 rounded-full bg-transparent">
        {/* Desktop bar: logo left / all nav right */}
        <div className="hidden lg:flex items-center justify-between py-2 w-full min-h-[50px] bg-white/95 backdrop-blur-md rounded-full shadow-md px-4">
          {/* Logo on the left */}
          <div className="flex-shrink-0 ml-3">
            <Link to="/" className="flex items-center group">
              <div className="logo-container w-12 h-12 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center shadow-md transition-all duration-300 group-hover:shadow-lg group-hover:scale-105 group-hover:border-gray-500">
                <img
                  src={siteData.logo}
                  alt={siteData.siteName}
                  className="h-6 w-6 object-contain transition-transform duration-300 group-hover:scale-110"
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' fill='%23ffffff'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='0.3em' fill='%23000000' font-size='18' font-weight='bold'%3EU%3C/text%3E%3C/svg%3E";
                  }}
                />
              </div>
            </Link>
          </div>

          {/* All navigation on the right */}
          <nav className="flex items-center space-x-7">
            {/* Main navigation items */}
            {mainItems.map((item, index) => (
              <div key={index} className="relative">
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => handleDropdownToggle(index)}
                      className={`nav-link flex items-center space-x-1 whitespace-nowrap`}
                    >
                      <span>{item.name}</span>
                      <svg
                        className={`w-3 h-3 transition-transform duration-200 ${
                          activeDropdown === index ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <div
                      className={`absolute top-full left-0 mt-2 w-56 bg-white/95 backdrop-blur-xl border border-gray-200/30 rounded-xl shadow-2xl py-2 z-50 transition-all duration-300 ${
                        activeDropdown === index
                          ? "opacity-100 translate-y-0 scale-100"
                          : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                      }`}
                    >
                      {item.dropdown.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.path}
                          // className="dropdown-item"
                          className="dropdown-item block px-4 py-2 whitespace-nowrap hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={(e) => {
                            setActiveDropdown(null);
                            // Handle page transition for events dropdown items
                            if (
                              subItem.path === "/events" ||
                              subItem.path.startsWith("/events#")
                            ) {
                              if (
                                startTransition &&
                                subItem.path === "/events"
                              ) {
                                e.preventDefault();
                                startTransition("/events");
                                return;
                              }
                              if (
                                location.pathname === "/events" &&
                                subItem.path.includes("#")
                              ) {
                                e.preventDefault();
                                const sectionId = subItem.path.split("#")[1];
                                const section =
                                  document.getElementById(sectionId);
                                if (section) {
                                  setTimeout(() => {
                                    section.scrollIntoView({
                                      behavior: "smooth",
                                      block: "start",
                                    });
                                  }, 100);
                                }
                              } else if (
                                subItem.path.includes("#") &&
                                startTransition
                              ) {
                                // If not on events page and link has hash, transition to events page first
                                e.preventDefault();
                                startTransition(subItem.path);
                                return;
                              }
                            }
                            // Handle page transition for alumni dropdown items
                            if (subItem.path === "/alumni" && startTransition) {
                              e.preventDefault();
                              startTransition("/alumni");
                              return;
                            }
                          }}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : item.external && item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-link whitespace-nowrap hover:text-black transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    className={`nav-link ${
                      isActive(item.path) ? "active" : ""
                    } whitespace-nowrap`}
                    onClick={(e) => handleNavigation(e, item)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg transition-all duration-300 text-gray-700 hover:text-black hover:bg-gray-100"
          >
            <svg
              className={`w-6 h-6 ${
                mobileMenuOpen ? "rotate-90" : ""
              } transition-transform duration-300`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
          {/* End desktop bar */}
        </div>

        {/* Mobile row with logo left and menu right */}
        <div className="flex lg:hidden items-center justify-between py-1 min-h-[42px] bg-white/95 backdrop-blur-md rounded-full shadow-md">
          <Link to="/" className="flex items-center group ml-2">
            <div className="w-8 h-8 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:border-gray-500 group-hover:shadow-md">
              <img
                src={siteData.logo}
                alt={siteData.siteName}
                className="h-5 w-5 object-contain transition-transform duration-300 group-hover:scale-110"
              />
            </div>
          </Link>
          <div className="flex items-center space-x-4">
            {/* Search icon removed from mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-700 lg:hover:text-black lg:hover:bg-gray-100 transition-all duration-300 mr-2"
            >
              <svg
                className={`w-6 h-6 ${
                  mobileMenuOpen ? "rotate-90" : ""
                } transition-transform duration-300`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl text-gray-700 rounded-2xl shadow-2xl mt-2 border border-gray-200/30">
            <nav className="p-4 space-y-2">
              {mainItems.map((item, index) => (
                <div key={index}>
                  {item.dropdown ? (
                    <>
                      <button
                        onClick={() => handleDropdownToggle(index)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left rounded-lg lg:hover:text-black transition-colors duration-300"
                      >
                        <span className="text-sm font-medium whitespace-nowrap">
                          {item.name}
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-300 ${
                            activeDropdown === index ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {activeDropdown === index && (
                        <div className="pl-3 space-y-1 mt-1 border-l border-gray-300">
                          {item.dropdown.map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.path}
                              className="block px-3 py-2 text-sm rounded-lg lg:hover:text-black whitespace-nowrap transition-colors duration-300"
                              onClick={(e) => {
                                setMobileMenuOpen(false);
                                // Handle page transition for events dropdown items
                                if (
                                  subItem.path === "/events" ||
                                  subItem.path.startsWith("/events#")
                                ) {
                                  if (
                                    startTransition &&
                                    subItem.path === "/events"
                                  ) {
                                    e.preventDefault();
                                    startTransition("/events");
                                    return;
                                  }
                                  if (
                                    location.pathname === "/events" &&
                                    subItem.path.includes("#")
                                  ) {
                                    e.preventDefault();
                                    const sectionId =
                                      subItem.path.split("#")[1];
                                    const section =
                                      document.getElementById(sectionId);
                                    if (section) {
                                      setTimeout(() => {
                                        section.scrollIntoView({
                                          behavior: "smooth",
                                          block: "start",
                                        });
                                      }, 100);
                                    }
                                  } else if (
                                    subItem.path.includes("#") &&
                                    startTransition
                                  ) {
                                    // If not on events page and link has hash, transition to events page first
                                    e.preventDefault();
                                    startTransition(subItem.path);
                                    return;
                                  }
                                }
                                // Handle page transition for alumni dropdown items
                                if (
                                  subItem.path === "/alumni" &&
                                  startTransition
                                ) {
                                  e.preventDefault();
                                  startTransition("/alumni");
                                  return;
                                }
                              }}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : item.external && item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-3 py-2 text-sm rounded-lg lg:hover:text-black whitespace-nowrap transition-colors duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="block px-3 py-2 text-sm rounded-lg lg:hover:text-black whitespace-nowrap transition-colors duration-300"
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        handleNavigation(e, item);
                      }}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              {/* Search icon removed from mobile menu */}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
